import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    BarChart3,
    CreditCard,
    Link2,
    Music2,
    Pencil,
    Users,
    Wallet,
} from "lucide-react";
import { memberPreview, permissionPreview } from "./teamsPreviewData";
import { useQueryClient } from "@tanstack/react-query";
import { PricingPlanModal } from "../../../components/PricingPlanModal";
import { TeamSeatPlanModal } from "../../../components/TeamSeatPlanModal";
import { BILLING_COMPLETE_EVENT } from "../../../helper/billingReturn";
import { useDebounce } from "../../../hooks/useDebounce";
import { useSearchArtist } from "../../../queries/artist.query";
import { useCreateWorkspaceArtist, useSwitchArtist, useWorkspaceArtists, useWorkspaceSummary } from "../../../queries/organization/workspace.query";
import { ArtistProfileCardSkeleton, BillingRowSkeleton, SkeletonBlock } from "./skeletons/TeamsSkeletons";

const ARTIST_AVATAR_CLASSES = ["bg-[#FA6138]", "bg-[#5B4DFF]", "bg-[#E85AA0]", "bg-[#2FBF71]", "bg-[#3D7EFF]"];

function artistInitials(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatMonthlyTotal(value) {
    if (value == null || value === "") return "—";
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "—";
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

function formatPlanPrice(price, cycle) {
    if (price == null) return "—";
    const period = String(cycle || "").toLowerCase() === "yearly" ? "year" : "month";
    return `$${formatMonthlyTotal(price)} / ${period}`;
}

function formatRenewalDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCreditCount(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "0";
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

function formatCreditsRemaining(artist) {
    if (artist.status === "pending_payment" || artist.creditsRemaining == null) return "—";
    const remaining = Number(artist.creditsRemaining);
    const total = Number(artist.creditsTotal);
    const hasCredits = (Number.isFinite(total) && total > 0) || (Number.isFinite(remaining) && remaining > 0);
    if (!hasCredits && artist.status === "limited_access") return "—";
    return `${formatCreditCount(remaining)} / ${formatCreditCount(total)}`;
}

function ArtistAvatar({ artist, className, textClassName = "text-xs" }) {
    const photo = artist?.avatar;
    if (photo) {
        return (
            <img
                src={photo}
                alt={artist?.name || "Artist"}
                className={`rounded-full object-cover ${className}`}
            />
        );
    }
    return (
        <span className={`grid place-items-center rounded-full font-bold text-white ${textClassName} ${artist?.avatarClass || "bg-theme_clr1"} ${className}`}>
            {artist?.initials || "—"}
        </span>
    );
}

function StatusPill({ status, showDot = false }) {
    if (status === "pending_removal") {
        return (
            <span className="inline-flex items-center rounded-full bg-[#F5C16C] px-2.5 py-0.5 text-[11px] font-semibold text-[#3d2a00]">
                Pending Removal
            </span>
        );
    }
    if (status === "pending_payment") {
        return (
            <span className="inline-flex items-center rounded-full bg-[#FFD7C8] px-2.5 py-0.5 text-[11px] font-semibold text-[#8A3418]">
                Payment Required
            </span>
        );
    }
    if (status === "limited_access") {
        return (
            <span className="inline-flex items-center rounded-full bg-[#D9DDF5] px-2.5 py-0.5 text-[11px] font-semibold text-[#1A1464]">
                Limited Access
            </span>
        );
    }
    if (status === "invited") {
        return (
            <span className="inline-flex items-center rounded-full bg-[#FFD7C8] px-2.5 py-0.5 text-[11px] font-semibold text-[#8A3418]">
                Invited
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#B7F3CF] px-2.5 py-0.5 text-[11px] font-semibold text-[#0d5a32]">
            {showDot && <span className="h-2 w-2 rounded-full bg-[#12B886]" />}
            Active
        </span>
    );
}

function SectionBar({ title, action, icon }) {
    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-theme_clr1 to-theme_clr2 px-4 py-3">
            <h2 className="flex items-center gap-2 text-sm font-bold text-white md:text-base">
                {icon}
                {title}
            </h2>
            {action}
        </div>
    );
}

function ArtistProfileCard({ artist, isCurrent, onSwitch, onPay, switchingId }) {
    return (
        <article className="flex h-full flex-col rounded-2xl bg-theme_clr1 p-4 text-white">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="relative">
                        <ArtistAvatar artist={artist} className="h-11 w-11" textClassName="text-sm" />
                        {isCurrent && (
                            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-theme_clr1 bg-[#3DDC84]" title="Current artist" />
                        )}
                    </span>
                    <div>
                        <h3 className="flex flex-wrap items-center gap-2 font-semibold">
                            {artist.name}
                            {isCurrent && (
                                <span className="text-[11px] font-medium text-[#3DDC84]">Current artist</span>
                            )}
                        </h3>
                        {artist.seatType === "Base Artist" ? (
                            <div className="mt-1 flex items-center gap-1.5">
                                <button type="button" className="inline-flex items-center rounded-full bg-gradient-to-r from-[#F8D56A] to-[#E5A000] px-2.5 py-0.5 text-[11px] font-bold text-[#4A3200]">
                                    Base Artist
                                </button>
                                <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-[#F5C451]" fill="currentColor" aria-hidden="true">
                                    <path d="M3 18h18v2.2H3V18zm.6-1.6L2.2 8.2l5.1 3.4L12 4.5l4.7 7.1 5.1-3.4-1.4 8.2H3.6z" />
                                </svg>
                            </div>
                        ) : (
                            <p className="text-xs text-white/70">Additional Artist</p>
                        )}
                    </div>
                </div>
                <StatusPill status={artist.status} showDot={isCurrent && artist.status === "active"} />
            </div>
            {artist.status === "pending_removal" && (
                <p className="mt-3 text-xs text-[#F5C16C]">Active until {artist.activeUntilLabel || "—"}</p>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                    <div className="text-xs text-white/60">Credits Remaining</div>
                    <div className="font-semibold">{formatCreditsRemaining(artist)}</div>
                </div>
                <div>
                    <div className="text-xs text-white/60">Next Renewal</div>
                    <div className="font-semibold">{artist.renewsOnLabel || "—"}</div>
                    <div className="mt-1 text-xs font-semibold text-white/90">{formatPlanPrice(artist.price, artist.cycle)}</div>
                </div>
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                {artist.status === "pending_payment" && (
                    <button
                        type="button"
                        onClick={() => onPay(artist)}
                        className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#1A1464]"
                    >
                        Complete Payment
                    </button>
                )}
                {artist.status === "limited_access" && (
                    <button
                        type="button"
                        onClick={() => onPay(artist)}
                        className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#1A1464]"
                    >
                        Reactivate
                    </button>
                )}
                {artist.status !== "pending_payment" && (
                    <button
                        type="button"
                        onClick={() => onSwitch(artist.id)}
                        disabled={isCurrent || Boolean(switchingId)}
                        className={`rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#1A1464] disabled:cursor-not-allowed ${isCurrent ? "opacity-45" : "disabled:opacity-60"}`}
                    >
                        {switchingId === artist.id ? "Switching…" : "Switch to Artist"}
                    </button>
                )}
                <button type="button" className="rounded-lg px-2 py-1.5 text-xs font-semibold text-[#FFB4A2]">
                    {artist.status === "pending_removal" ? "Cancel Removal" : "Remove Artist"}
                </button>
            </div>
        </article>
    );
}

function AddArtistModal({ open, saving, existingSongstatsIds, onClose, onConfirm }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedArtist, setSelectedArtist] = useState(null);
    const debouncedSearch = useDebounce(searchTerm, 500);
    const { data: searchResults = [], isLoading: isSearching, isFetching } = useSearchArtist({
        name: debouncedSearch,
        enabled: open && !!debouncedSearch.trim() && !selectedArtist,
    });
    const showResults = !!debouncedSearch.trim() && !selectedArtist;

    useEffect(() => {
        if (open) return;
        setSearchTerm("");
        setSelectedArtist(null);
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-md rounded-2xl bg-theme_clr1 p-5 text-white shadow-xl">
                <h2 className="font-manrope text-xl font-bold">{selectedArtist ? "Confirm Artist" : "Add Artist"}</h2>
                {selectedArtist ? (
                    <>
                        <p className="mt-1 text-sm text-white/75">Make sure you've selected the correct artist before continuing.</p>
                        <div className="mt-4 flex items-center gap-3 rounded-xl bg-white px-3 py-3 text-theme_clr1">
                            {selectedArtist.avatar ? (
                                <img src={selectedArtist.avatar} alt="" className="h-11 w-11 rounded-full object-cover" />
                            ) : (
                                <span className="grid h-11 w-11 place-items-center rounded-full bg-theme_clr1 text-sm font-bold text-white">
                                    {artistInitials(selectedArtist.name)}
                                </span>
                            )}
                            <div className="font-semibold">{selectedArtist.name}</div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setSelectedArtist(null)}
                                disabled={saving}
                                className="rounded-lg px-3 py-2 text-sm font-semibold text-white/80"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={() => onConfirm(selectedArtist)}
                                disabled={saving}
                                className="rounded-lg bg-theme_clr2 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {saving ? "Saving…" : "Continue"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="mt-1 text-sm text-white/75">
                            Search for an artist to add to your workspace.
                        </p>
                        <label htmlFor="artist-search" className="mt-4 block text-xs text-white/70">Search for an Artist</label>
                        <input
                            id="artist-search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Enter an artist name"
                            autoFocus
                            className="mt-1 w-full rounded-lg border border-white/20 bg-white px-3 py-2 text-sm text-theme_clr1 outline-none"
                        />
                        {showResults && (
                            <div className="mt-2 max-h-60 overflow-y-auto rounded-lg bg-white p-2 text-theme_clr1">
                                {(isSearching || isFetching) && (
                                    <p className="px-3 py-2 text-sm text-gray-600">Searching...</p>
                                )}
                                {!isSearching && !isFetching && searchResults.map((artist) => {
                                    const alreadyAdded = existingSongstatsIds.has(artist.songstats_artist_id);
                                    return (
                                        <button
                                            key={artist.songstats_artist_id || artist.name}
                                            type="button"
                                            disabled={alreadyAdded}
                                            onClick={() => setSelectedArtist(artist)}
                                            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                                        >
                                            {artist.avatar ? (
                                                <img src={artist.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                                            ) : (
                                                <span className="grid h-9 w-9 place-items-center rounded-full bg-theme_clr1 text-xs font-bold text-white">
                                                    {artistInitials(artist.name)}
                                                </span>
                                            )}
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-semibold capitalize">{artist.name}</span>
                                                {alreadyAdded && <span className="text-xs text-gray-500">This artist is already in your workspace.</span>}
                                            </span>
                                        </button>
                                    );
                                })}
                                {!isSearching && !isFetching && searchResults.length === 0 && (
                                    <p className="px-3 py-2 text-sm text-gray-600">No artists found</p>
                                )}
                            </div>
                        )}
                        <div className="mt-4 flex justify-end">
                            <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-semibold text-white/80">
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function ArtistCarouselArrow({ direction, visible, onClick }) {
    const isPrevious = direction === "prev";
    return (
        <button
            type="button"
            aria-label={isPrevious ? "Previous artists" : "Next artists"}
            aria-hidden={!visible}
            tabIndex={visible ? 0 : -1}
            onClick={onClick}
            className={`grid h-8 place-items-center self-center overflow-hidden rounded-full bg-theme_clr2 text-white shadow-sm transition-all duration-300 ease-out ${
                visible ? "mx-2 w-8 min-w-[2rem] opacity-100" : "pointer-events-none mx-0 w-0 min-w-0 opacity-0"
            }`}
        >
            <i className={`bi ${isPrevious ? "bi-chevron-left" : "bi-chevron-right"} text-sm`} />
        </button>
    );
}

function ArtistProfilesSection({
    artists,
    artistsLoading,
    activeArtistId,
    switchingId,
    onSwitch,
    onPay,
    onAdd,
}) {
    const artistCarouselRef = useRef(null);
    const [visibleArtistCount, setVisibleArtistCount] = useState(1);
    const [artistSlide, setArtistSlide] = useState(0);
    const maxArtistSlide = Math.max(artists.length - visibleArtistCount, 0);
    const artistFillerCount = artistsLoading || artists.length >= visibleArtistCount
        ? 0
        : Math.max(visibleArtistCount - artists.length, 0);
    const showPreviousArtists = maxArtistSlide > 0 && artistSlide > 0;
    const showNextArtists = maxArtistSlide > 0 && artistSlide < maxArtistSlide;
    const artistCardWidth = `calc((100% - ${(visibleArtistCount - 1)}rem) / ${visibleArtistCount})`;

    useEffect(() => {
        const node = artistCarouselRef.current;
        if (!node || typeof ResizeObserver === "undefined") return undefined;
        const applyWidth = (width) => {
            if (width < 640) setVisibleArtistCount(1);
            else if (width < 960) setVisibleArtistCount(2);
            else setVisibleArtistCount(3);
        };
        applyWidth(node.clientWidth);
        const observer = new ResizeObserver((entries) => {
            applyWidth(entries[0]?.contentRect?.width || 0);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        setArtistSlide((current) => Math.min(current, maxArtistSlide));
    }, [maxArtistSlide]);

    return (
        <section>
            <SectionBar
                title="Artist Profiles"
                icon={(
                    <span className="grid h-6 w-6 place-items-center rounded-full border border-white/80 text-xs">
                        <i className="bi bi-person-circle" />
                    </span>
                )}
                action={(
                    <button
                        type="button"
                        onClick={onAdd}
                        className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-theme_clr1 shadow-sm"
                    >
                        Add Artist +
                    </button>
                )}
            />
            <div ref={artistCarouselRef} className="flex items-stretch">
                <ArtistCarouselArrow
                    direction="prev"
                    visible={showPreviousArtists}
                    onClick={() => setArtistSlide((current) => Math.max(0, current - 1))}
                />
                <div className="min-w-0 flex-1 overflow-hidden transition-[width] duration-300 ease-out">
                    {artistsLoading ? (
                        <div className="flex items-stretch gap-4" aria-hidden="true">
                            {Array.from({ length: visibleArtistCount }).map((_, index) => (
                                <div key={`artist-skeleton-${index}`} className="shrink-0" style={{ width: artistCardWidth }}>
                                    <ArtistProfileCardSkeleton />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            className="flex items-stretch gap-4 transition-transform duration-300 ease-out"
                            style={{ transform: `translateX(calc(${artistSlide} * -1 * ((100% + 1rem) / ${visibleArtistCount})))` }}
                        >
                            {artists.map((artist) => (
                                <div key={artist.id} className="shrink-0 transition-[width] duration-300 ease-out" style={{ width: artistCardWidth }}>
                                    <ArtistProfileCard
                                        artist={artist}
                                        isCurrent={artist.id === activeArtistId}
                                        onSwitch={onSwitch}
                                        onPay={onPay}
                                        switchingId={switchingId}
                                    />
                                </div>
                            ))}
                            {Array.from({ length: artistFillerCount }).map((_, index) => (
                                <article
                                    key={`add-artist-slot-${index}`}
                                    className="flex min-h-[220px] shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-white/25 bg-theme_clr1 p-4 text-center text-white transition-[width] duration-300 ease-out"
                                    style={{ width: artistCardWidth }}
                                >
                                    <span className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-lg">
                                        <i className="bi bi-plus-lg" />
                                    </span>
                                    <p className="text-sm font-semibold">Add an artist</p>
                                    <p className="mt-1 max-w-[200px] text-xs text-white/70">
                                        This space stays open until you add another artist profile.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={onAdd}
                                        className="mt-4 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-theme_clr1"
                                    >
                                        Add Artist
                                    </button>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
                <ArtistCarouselArrow
                    direction="next"
                    visible={showNextArtists}
                    onClick={() => setArtistSlide((current) => Math.min(maxArtistSlide, current + 1))}
                />
            </div>
            <div className="mt-4 rounded-xl bg-[#FA6138] px-4 py-3 text-sm text-white">
                Adding an additional artist charges $20 immediately and gives that artist 50 monthly credits. Artist substitutions are not allowed. Remove an artist before adding a new one.
            </div>
        </section>
    );
}

function WorkspaceSummary({ workspace, workspaceLoading }) {
    return (
        <section className="grid gap-4 lg:grid-cols-3" aria-busy={workspaceLoading}>
            <article className="rounded-2xl bg-theme_clr1 p-4 text-white">
                <div className="mb-3 flex items-center gap-2 text-sm text-white/70">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#3A2A6A] text-[#C4B5FD]">
                        <i className="bi bi-building" />
                    </span>
                    Workspace
                </div>
                {workspaceLoading ? (
                    <div aria-hidden="true">
                        <SkeletonBlock className="h-6 w-40" />
                        <SkeletonBlock className="mt-2 h-4 w-24" />
                        <SkeletonBlock className="mt-4 h-3 w-48" />
                    </div>
                ) : (
                    <>
                        <h3 className="font-manrope text-xl font-bold">{workspace?.name || "—"}</h3>
                        <p className="mt-1 text-sm text-[#8EB4FF]">{workspace?.accountType || "—"}</p>
                        <p className="mt-4 text-xs text-white/70">
                            Owner: {workspace?.ownerEmail || "—"}
                        </p>
                    </>
                )}
            </article>
            <article className="rounded-2xl bg-theme_clr1 p-4 text-white">
                <div className="mb-3 flex items-center gap-2 text-sm text-white/70">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#143D32] text-[#6EE7B7]">
                        <i className="bi bi-people" />
                    </span>
                    Team
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        {workspaceLoading ? <SkeletonBlock className="h-9 w-12" /> : <div className="font-manrope text-3xl font-bold">{workspace?.userCount ?? 0}</div>}
                        <div className="text-sm text-white/70">Users</div>
                    </div>
                    <div>
                        {workspaceLoading ? <SkeletonBlock className="h-9 w-12" /> : <div className="font-manrope text-3xl font-bold">{workspace?.artistCount ?? 0}</div>}
                        <div className="text-sm text-white/70">Artists</div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => document.getElementById("team-members")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="mt-4 text-sm font-semibold text-white"
                >
                    View all members →
                </button>
            </article>
            <article className="rounded-2xl bg-theme_clr1 p-4 text-white">
                <div className="mb-3 flex items-center gap-2 text-sm text-white/70">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#4A3810] text-[#F5C451]">
                        <i className="bi bi-credit-card" />
                    </span>
                    Billing
                </div>
                {workspaceLoading ? (
                    <SkeletonBlock className="h-9 w-36" />
                ) : (
                    <div className="font-manrope text-3xl font-bold">${formatMonthlyTotal(workspace?.monthlyTotal)}<span className="text-base font-medium text-white/70"> / month</span></div>
                )}
                <p className="mt-2 text-xs text-white/70">Current monthly total</p>
                <p className="mt-1 text-xs text-white/70">
                    Next renewal: {workspaceLoading ? (
                        <SkeletonBlock className="ml-1 inline-block h-3 w-24 align-middle" />
                    ) : (
                        <span className="font-semibold text-theme_clr2">{formatRenewalDate(workspace?.nextRenewal) || "—"}</span>
                    )}
                </p>
            </article>
        </section>
    );
}

const permissionIcons = {
    team: Users,
    artists: Music2,
    billing: CreditCard,
    financials: Wallet,
    growth: BarChart3,
    links: Link2,
};

function TeamMembersSection() {
    return (
        <section id="team-members">
            <SectionBar
                title="Team Members"
                action={<span className="text-sm font-bold text-white">Invite Team Member +</span>}
            />
            <div className="overflow-hidden rounded-2xl bg-theme_clr1 text-white">
                <div className="overflow-x-auto">
                    <table className="min-w-[860px] w-full text-left text-sm">
                        <thead className="text-xs uppercase tracking-wide text-white/60">
                            <tr>
                                <th className="px-4 py-3 font-medium">Member</th>
                                <th className="px-4 py-3 font-medium">Role</th>
                                <th className="px-4 py-3 font-medium">Permissions</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 font-medium">Joined</th>
                                <th className="px-4 py-3 font-medium">Last Active</th>
                                <th className="px-4 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {memberPreview.map((member) => (
                                <tr key={member.id} className="border-t border-white/10">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <span className={`grid h-9 w-9 place-items-center rounded-full text-xs font-bold ${member.avatarClass}`}>
                                                {member.initials}
                                            </span>
                                            <div>
                                                <div className="font-semibold">{member.name}</div>
                                                <div className="text-xs text-white/60">{member.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">{member.role}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            {permissionPreview.map((permission) => {
                                                const Icon = permissionIcons[permission.key];
                                                return (
                                                    <span
                                                        key={permission.key}
                                                        title={permission.label}
                                                        className="grid h-7 w-7 place-items-center rounded-md bg-white/10 text-white"
                                                    >
                                                        <Icon className="h-3.5 w-3.5" />
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3"><StatusPill status={member.status} /></td>
                                    <td className="px-4 py-3 text-white/80">{member.joined}</td>
                                    <td className="px-4 py-3 text-white/80">{member.lastActive}</td>
                                    <td className="px-4 py-3">
                                        {member.status === "invited" ? (
                                            <button type="button" className="rounded-full bg-[#FA6138] px-3 py-1.5 text-xs font-semibold">
                                                Resend Invite
                                            </button>
                                        ) : (
                                            <button type="button" className="grid h-8 w-8 place-items-center rounded-lg bg-white/10" aria-label={`Edit ${member.name}`}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between border-t border-white/10 px-4 py-3 text-xs text-white/70">
                    <span>Show 5 per page</span>
                    <span>1–4 of 4</span>
                </div>
            </div>
        </section>
    );
}

const seatLabel = {
    "Base Artist": "Base",
    "Additional Artist": "Additional",
};

function BillingOverview({ artists, artistsLoading }) {
    return (
        <section>
            <SectionBar title="Billing Overview" />
            <div className="overflow-hidden rounded-2xl bg-theme_clr1 text-white">
                <div className="overflow-x-auto">
                    <table className="min-w-[720px] w-full text-left text-sm">
                        <thead className="text-xs uppercase tracking-wide text-white/60">
                            <tr>
                                <th className="px-4 py-3 font-medium">Artist</th>
                                <th className="px-4 py-3 font-medium">Type</th>
                                <th className="px-4 py-3 font-medium">Price</th>
                                <th className="px-4 py-3 font-medium">Billing Cycle</th>
                                <th className="px-4 py-3 font-medium">Next Renewal</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {artistsLoading && Array.from({ length: 3 }).map((_, index) => (
                                <BillingRowSkeleton key={`billing-skeleton-${index}`} />
                            ))}
                            {!artistsLoading && artists.length === 0 && (
                                <tr className="border-t border-white/10">
                                    <td className="px-4 py-3 text-white/70" colSpan={6}>
                                        No artist profiles in this workspace yet.
                                    </td>
                                </tr>
                            )}
                            {!artistsLoading && artists.map((artist) => (
                                <tr key={artist.id} className="border-t border-white/10">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <ArtistAvatar artist={artist} className="h-8 w-8" textClassName="text-[11px]" />
                                            {artist.name}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">{seatLabel[artist.seatType]} Artist</td>
                                    <td className="px-4 py-3">{formatPlanPrice(artist.price, artist.cycle)}</td>
                                    <td className="px-4 py-3">{artist.cycle}</td>
                                    <td className="px-4 py-3">{artist.renewsOnLabel || "—"}</td>
                                    <td className="px-4 py-3"><StatusPill status={artist.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="mt-4 rounded-xl bg-[#FA6138] px-4 py-3 text-sm text-white">
                Users are not billed. Only artist profiles are billed based on their type and renewal date.
            </div>
        </section>
    );
}

function OutlineButton({ children, onClick, type = "button" }) {
    return (
        <button
            type={type}
            onClick={onClick}
            className="rounded-full border border-white/80 bg-white px-4 py-2 text-sm font-semibold text-[#1A1464] transition hover:bg-white/90"
        >
            {children}
        </button>
    );
}

function TeamsHeader({
    artistsLoading,
    switchingId,
    switcherRef,
    switcherOpen,
    onToggleSwitcher,
    activeArtist,
    activeArtistId,
    switchableArtists,
    onChooseArtist,
    onAddArtist,
}) {
    return (
        <section className="relative z-30 rounded-2xl bg-gradient-to-r from-theme_clr1 to-theme_clr2 p-4 text-white lg:p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="font-manrope text-2xl font-bold">Teams</h1>
                    <p className="mt-1 max-w-xl text-sm text-white/90">
                        Manage your workspace users, artist profiles, permissions, and billing.
                    </p>
                </div>
                <div className="relative w-full sm:w-auto" ref={switcherRef}>
                    {artistsLoading ? (
                        <div className="flex w-full items-center gap-3 rounded-full border-2 border-theme_clr1 bg-white px-3 py-1.5 sm:w-56" aria-hidden="true">
                            <SkeletonBlock tone="dark" className="h-8 w-8 rounded-full" />
                            <SkeletonBlock tone="dark" className="h-4 flex-1 sm:w-24 sm:flex-none" />
                        </div>
                    ) : (
                        <button
                            type="button"
                            disabled={Boolean(switchingId)}
                            onClick={onToggleSwitcher}
                            className="flex w-full items-center gap-3 rounded-full border-2 border-theme_clr1 bg-white px-3 py-1.5 text-sm font-semibold text-theme_clr1 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                        >
                            <ArtistAvatar artist={activeArtist} className="h-8 w-8 shrink-0 border-2 border-theme_clr1" />
                            <span className="min-w-0 flex-1 truncate text-left sm:flex-none">Switch Artist</span>
                            <i className="bi bi-chevron-down shrink-0 text-xs" />
                        </button>
                    )}
                    {switcherOpen && (
                        <div className="absolute left-0 right-0 z-50 mt-2 max-h-60 origin-top-right overflow-y-auto rounded-md bg-white text-[#1A1464] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:left-auto sm:w-56">
                            {switchableArtists.length === 0 && (
                                <p className="px-4 py-3 text-sm text-[#1A1464]/70">No artists yet</p>
                            )}
                            {switchableArtists.map((artist) => (
                                <button
                                    key={artist.id}
                                    type="button"
                                    disabled={Boolean(switchingId)}
                                    onClick={() => onChooseArtist(artist.id)}
                                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-[#F4F6FF] disabled:cursor-not-allowed disabled:opacity-60 ${artist.id === activeArtistId ? "bg-[#EEF1FF] font-semibold" : ""}`}
                                >
                                    <ArtistAvatar artist={artist} className="h-7 w-7 shrink-0 border-2 border-theme_clr1" textClassName="text-[11px]" />
                                    <span className="min-w-0 flex-1 truncate">{artist.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
                <OutlineButton onClick={onAddArtist}>
                    <i className="bi bi-music-note-beamed mr-2" />
                    Add Artists
                </OutlineButton>
                <OutlineButton>
                    <i className="bi bi-plus-circle mr-2" />
                    Add Members
                </OutlineButton>
            </div>
        </section>
    );
}

export const Teams = () => {
    const queryClient = useQueryClient();
    const { data: workspace, isLoading: workspaceLoading } = useWorkspaceSummary();
    const { data: artistData, isLoading: artistsLoading } = useWorkspaceArtists();
    const switchArtist = useSwitchArtist();
    const createArtist = useCreateWorkspaceArtist();
    const artists = useMemo(
        () => (artistData?.artists || []).map((artist, index) => ({
            ...artist,
            initials: artistInitials(artist.name),
            avatarClass: ARTIST_AVATAR_CLASSES[index % ARTIST_AVATAR_CLASSES.length],
            renewsOnLabel: formatRenewalDate(artist.renewsOn),
            activeUntilLabel: formatRenewalDate(artist.activeUntil),
        })),
        [artistData]
    );
    const [activeArtistId, setActiveArtistId] = useState(null);
    const [switchingId, setSwitchingId] = useState(null);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [addArtistOpen, setAddArtistOpen] = useState(false);
    const [payArtist, setPayArtist] = useState(null);
    const existingSongstatsIds = useMemo(
        () => new Set(artists.map((artist) => artist.songstatsArtistId).filter(Boolean)),
        [artists]
    );
    const switcherRef = useRef(null);
    const switchableArtists = artists.filter((artist) => artist.status !== "pending_payment");
    const activeArtist = switchableArtists.find((artist) => artist.id === activeArtistId) || switchableArtists[0] || null;

    useEffect(() => {
        if (!artistData?.artists) return;
        setActiveArtistId((current) => {
            const switchable = artistData.artists.filter((artist) => artist.status !== "pending_payment");
            const ids = new Set(switchable.map((artist) => artist.id));
            if (current && ids.has(current)) return current;
            if (artistData.activeArtistProfileId && ids.has(artistData.activeArtistProfileId)) {
                return artistData.activeArtistProfileId;
            }
            return switchable[0]?.id || null;
        });
    }, [artistData]);

    useEffect(() => {
        const close = (event) => {
            if (switcherRef.current && !switcherRef.current.contains(event.target)) {
                setSwitcherOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    useEffect(() => {
        let timers = [];
        const refreshArtists = () => {
            setPayArtist(null);
            const refresh = () => {
                queryClient.invalidateQueries({ queryKey: ["workspace-artists"] });
                queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
            };
            timers.forEach((timer) => window.clearTimeout(timer));
            refresh();
            // Stripe marks the artist active a moment after the browser returns.
            timers = [2000, 5000, 10000].map((delay) => window.setTimeout(refresh, delay));
        };
        window.addEventListener(BILLING_COMPLETE_EVENT, refreshArtists);
        return () => {
            timers.forEach((timer) => window.clearTimeout(timer));
            window.removeEventListener(BILLING_COMPLETE_EVENT, refreshArtists);
        };
    }, [queryClient]);

    const openAddArtist = () => {
        setAddArtistOpen(true);
    };

    const confirmNewArtist = async (selectedArtist) => {
        const songstatsArtistId = selectedArtist?.songstats_artist_id;
        const displayName = String(selectedArtist?.name || "").trim();
        if (!songstatsArtistId || !displayName || createArtist.isPending) return;
        if (existingSongstatsIds.has(songstatsArtistId)) return;
        try {
            const created = await createArtist.mutateAsync({
                displayName,
                songstatsArtistId,
                avatar: selectedArtist.avatar || "",
            });
            setAddArtistOpen(false);
            if (created?.id) {
                setPayArtist({ id: created.id, checkoutSeat: created.checkoutSeat || "addon" });
            }
        } catch {
            // The mutation shows the error.
        }
    };

    const startPayment = (artist) => {
        if (!artist?.id) return;
        setPayArtist({
            id: artist.id,
            checkoutSeat: artist.checkoutSeat || (artist.seatType === "Base Artist" ? "main" : "addon"),
        });
    };

    const chooseArtist = (artistId) => {
        setSwitcherOpen(false);
        const selected = artists.find((artist) => artist.id === artistId);
        if (!artistId || artistId === activeArtistId || switchingId || selected?.status === "pending_payment") return;
        const previousId = activeArtistId;
        const name = artists.find((artist) => artist.id === artistId)?.name;
        setActiveArtistId(artistId);
        setSwitchingId(artistId);
        switchArtist.mutate(
            { artistProfileId: artistId, name },
            {
                onSuccess: () => setSwitchingId(null),
                onError: () => {
                    setActiveArtistId(previousId);
                    setSwitchingId(null);
                },
            }
        );
    };

    return (
        <div className="Teams-Page py-4">
            <div className="container max-w-full space-y-4 2xl:space-y-5">
                <TeamsHeader
                    artistsLoading={artistsLoading}
                    switchingId={switchingId}
                    switcherRef={switcherRef}
                    switcherOpen={switcherOpen}
                    onToggleSwitcher={() => setSwitcherOpen((open) => !open)}
                    activeArtist={activeArtist}
                    activeArtistId={activeArtistId}
                    switchableArtists={switchableArtists}
                    onChooseArtist={chooseArtist}
                    onAddArtist={openAddArtist}
                />
                <WorkspaceSummary workspace={workspace} workspaceLoading={workspaceLoading} />
                <ArtistProfilesSection
                    artists={artists}
                    artistsLoading={artistsLoading}
                    activeArtistId={activeArtistId}
                    switchingId={switchingId}
                    onSwitch={chooseArtist}
                    onPay={startPayment}
                    onAdd={openAddArtist}
                />
                <TeamMembersSection />
                <BillingOverview artists={artists} artistsLoading={artistsLoading} />
            </div>
            <AddArtistModal
                open={addArtistOpen}
                saving={createArtist.isPending}
                existingSongstatsIds={existingSongstatsIds}
                onClose={() => setAddArtistOpen(false)}
                onConfirm={confirmNewArtist}
            />
            <PricingPlanModal
                isOpen={payArtist?.checkoutSeat === "main"}
                onClose={() => setPayArtist(null)}
                returnUrl={`${window.location.origin}/admin/teams`}
                artistProfileId={payArtist?.id}
            />
            <TeamSeatPlanModal
                isOpen={payArtist?.checkoutSeat === "addon"}
                onClose={() => setPayArtist(null)}
                returnUrl={`${window.location.origin}/admin/teams`}
                artistProfileId={payArtist?.id}
            />
        </div>
    );
};
